/**
 * `/progress` — for any status, what produced it (#33, P1.8; §3, §4.8).
 *
 * **Amended 2026-11-01 (#550):** this page used to list the Phase 1 Italian
 * objective with its per-objective evidence chain. The pilot is maths only
 * (D30), so that list is gone from the web for every account; the server's
 * Phase 1 endpoints remain. §6.2 still forbids the frontend calculating
 * status, evidence quality, or independence: every value is read off a response.
 *
 * **The top section (#441, §4.8).** `CourseTopics` comes straight after the
 * scope note: the focused course's topics under the five status words, one
 * next step per topic. It renders only once the summary has loaded, because
 * `CourseTopicsResponse` carries no `scopeOfClaim` of its own (SEC-027 below).
 *
 * **SEC-027 (D11, #39).** `scopeOfClaim` is rendered unconditionally, directly
 * under the heading, whenever the summary loads: never behind a click or
 * developer mode, and not only when an objective has evidence to show.
 * D11 declined SEC-020: no session records
 * whether every answer-revealing help was observable, so every conclusion
 * this instrument draws is scoped to help it itself delivered or observed.
 * A status shown without that sentence beside it asserts more than the
 * instrument can support, and the sentence is D11's own framing — not a
 * warning, not an integrity notice; roadmap §4.17 rejects copy written as
 * policing.
 *
 * The five-label legend below the objectives is unchanged from Phase 0: it
 * is where the temptation to add a number first appears, and it stays as the
 * standing answer to it even now that the page has real content above it.
 * `no-percentage.test.tsx` sweeps this whole route, objectives and legend
 * alike.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { queryKeys } from '../../api/query-keys.js';
import { useCourseFocus } from '../../app/course-focus.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { courseTitle } from '../../app/course-names.js';
import { localizedReason } from '../../i18n/status-reasons.js';
import { defineMessages } from '../../i18n/messages.js';
import { STATUS_DISPLAY_ORDER, statusLabel } from '../../i18n/status-labels.js';
import { CourseTopics } from './course-topics.js';

const messages = defineMessages({
  en: {
    heading: 'Progress',
    loading: 'Loading your progress…',
    foot: 'Homework shows what you can do with notes and friends.',
    footEmphasis: 'On your own',
    footEnd: 'only counts what DaVinci sees you do.',
    legendHeading: 'What DaVinci will report',
    legendNote:
      'Every objective sits at one of five labels. A label is a claim about what the evidence shows, so each one names the evidence that would produce it.',
  },
  he: {
    heading: 'התקדמות',
    loading: 'טוען את ההתקדמות שלך…',
    foot: 'שיעורי הבית מראים מה אתם יכולים לעשות עם מחברת וחברים.',
    footEmphasis: 'בכוחות עצמכם',
    footEnd: 'נספר רק מה ש-DaVinci רואה אתכם עושים.',
    legendHeading: 'מה DaVinci מדווחת',
    legendNote:
      'כל יעד נמצא באחת מחמש תוויות. תווית היא טענה על מה שהראיות מראות, ולכן כל אחת נקשרת לראיות שמביאות אליה.',
  },
});

export function ProgressPage(): React.JSX.Element {
  const api = useApi();
  const t = useMessages(messages);
  const { locale } = useLocale();
  const { focused } = useCourseFocus();
  const summary = useQuery({
    queryKey: queryKeys.progressSummary,
    queryFn: () => api.getProgressSummary(),
  });

  return (
    <div className="page progress-page">
      <h1>{focused === null ? t.heading : <bdi dir="auto">{courseTitle(focused, locale)}</bdi>}</h1>

      {/* SEC-027 (D11): the scope of the claim, directly under the heading, in muted small text. Never gated behind a click or developer mode. */}
      {summary.isSuccess ? (
        <p className="progress-scope" role="note">
          {localizedReason(summary.data.scopeOfClaim, locale)}
        </p>
      ) : null}

      {summary.isPending ? <p aria-live="polite">{t.loading}</p> : null}

      {summary.isError ? (
        <FailureNotice error={summary.error} onRetry={() => void summary.refetch()} />
      ) : null}

      {summary.isSuccess ? (
        <>
          <CourseTopics />
          <p className="progress-foot" role="note">
            {t.foot} <em>{t.footEmphasis}</em> {t.footEnd}
          </p>
        </>
      ) : null}

      <details className="progress-legend">
        <summary>
          <h2>{t.legendHeading}</h2>
        </summary>
        <p className="page-note">{t.legendNote}</p>
        {/*
         * A description list, not a table or a ranked list: `<dl>` says "term
         * and its definition", which is what these are. An ordered list would
         * assert a rank, and a rank is the first step towards a score.
         */}
        <dl>
          {STATUS_DISPLAY_ORDER.map((status) => {
            const { label, description } = statusLabel(status, locale);
            return (
              <div key={status}>
                <dt>
                  <strong>{label}</strong>
                </dt>
                <dd>{description}</dd>
              </div>
            );
          })}
        </dl>
      </details>
    </div>
  );
}
